import { __awaiter } from "tslib";
import { createHash } from 'crypto';
import createDebug from 'debug';
import { formatOptionUsage, loading } from './terminal_buffer';
import { Proof } from '../../database';
const debug = createDebug('bb:prove');
const commandOptions = {
    v: {
        longName: 'verify',
        description: "Verify the proof after it's been created.",
    },
};
const logUsage = (buf, msg = '') => __awaiter(void 0, void 0, void 0, function* () {
    let texts = [];
    if (msg) {
        if (typeof msg === 'string') {
            texts.push({ text: msg });
        }
        else {
            texts = texts.concat(msg);
        }
    }
    texts = texts.concat([
        { text: 'usage: prove <system> <witness> [<option>]' },
        { text: '' },
        { text: '  · system                  Alias of the system.' },
        { text: '  · witness                 Array of n variables in this format:' },
        { text: '                            [<v1>,<v2>,...<vn>]' },
        { text: '' },
        { text: 'Options:' },
        ...Object.keys(commandOptions)
            .map(name => formatOptionUsage(name, commandOptions[name]))
            .map(text => ({ text })),
        { text: '' },
        {
            text: [
                { text: 'For example, to generate a proof for the system with a single constraint ' },
                { text: 'x + y = z', color: 'magenta', bold: true },
                { text: ' with values 2 + 5 = 7, run:' },
            ],
            color: 'grey',
        },
        {
            text: [{ text: '`' }, { text: 'prove systemAlias [2, 5, 7]', color: 'grey', bold: true }, { text: '`' }],
        },
        { text: '' },
    ]);
    yield buf.log(texts);
});
const getWitness = (argv) => {
    const [witness] = argv.join('').match(/[\[|{](-?\d+,){0,}-?\d+\s*[\]|}]/) || [];
    if (!witness) {
        return null;
    }
    const values = witness
        .slice(1, -1)
        .split(',')
        .map(v => +v);
    if (values.some(v => Number.isNaN(v))) {
        return null;
    }
    return values;
};
const generateProofId = (proofData) => {
    return createHash('sha256').update(proofData).digest().toString('hex').slice(0, 8);
};
const run = (buf, app) => __awaiter(void 0, void 0, void 0, function* () {
    const { argv, options } = buf;
    const [, alias, ...rest] = argv;
    if (!alias || alias.match(/^[{|\[]/)) {
        yield logUsage(buf, [{ text: rest.length ? 'Invalid arguments.' : 'Too few arguments.', color: 'red' }]);
        return;
    }
    const witness = getWitness(rest);
    if (!witness) {
        yield buf.log([{ text: 'Invalid witness format.', color: 'red' }]);
        return;
    }
    const composer = yield app.getComposerWithAlias(alias);
    if (!composer) {
        yield buf.log([{ text: `System not found. Alias: ${alias}.`, color: 'red' }]);
        return;
    }
    const { constraintSystem } = composer;
    if (witness.length !== constraintSystem.varnum) {
        yield buf.log([
            {
                text: `Wrong number of witness values. Expected ${constraintSystem.varnum}. Got ${witness.length}.`,
                color: 'red',
            },
        ]);
        return;
    }
    const startTimestamp = Date.now();
    const loader = loading(buf);
    loader.start();
    let proof;
    let proofId;
    let error;
    try {
        const proofData = yield app.prove(composer, witness);
        proofId = generateProofId(proofData);
        proof = new Proof(proofId, composer.id, witness, proofData);
        yield app.addProof(proof);
    }
    catch (err) {
        debug(err);
        error = err.toString();
    }
    yield loader.stop();
    if (error || !proof) {
        yield buf.log([{ text: error || 'Failed to generate proof.', color: 'red' }]);
        return;
    }
    const shouldVerify = options.v;
    let verified;
    if (shouldVerify) {
        try {
            verified = yield app.verify(composer, proof.proofData);
            yield app.updateProofResult(proof.pid, verified);
        }
        catch (err) {
            debug(err);
            error = err.toString();
        }
        if (error) {
            yield buf.log([{ text: error || 'Failed to verify proof.', color: 'red' }]);
            return;
        }
    }
    yield buf.log([
        { text: `Successfully generated a proof.`, color: 'green' },
        { text: '' },
        {
            text: [{ text: 'System:      ' }, { text: alias }],
        },
        {
            text: [{ text: 'Witness:     ' }, { text: `[${witness.join(', ')}]` }],
        },
        {
            text: [{ text: 'Proof id:    ' }, { text: `0x${proofId}`, bold: true, color: 'yellow' }],
        },
        {
            text: [{ text: 'Time:        ' }, { text: `${(Date.now() - startTimestamp) / 1000}s`, bold: true }],
        },
        shouldVerify
            ? {
                text: [
                    { text: 'Status:      ' },
                    { text: verified ? 'VERIFIED' : 'FAILED', color: verified ? 'green' : 'red', bold: true },
                ],
            }
            : null,
        { text: '' },
    ].filter(t => !!t));
});
export const prove = {
    run,
    logUsage,
    commandOptions,
};
//# sourceMappingURL=data:application/json;base64,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